// src/telehealth/TelehealthInbox.jsx
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  orderBy,
  limit,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../../firebase";
import { useNavigate } from "react-router-dom";
import AppointmentChat from "./AppointmentChat";

// Preserved color configuration with enhanced features
const statusConfig = {
  confirmed: {
    label: "Confirmed",
    color: "#10b981",
    bg: "rgba(16, 185, 129, 0.1)",
    borderColor: "rgba(16, 185, 129, 0.3)",
    icon: "✓",
  },
  pending: {
    label: "Pending",
    color: "#f59e0b",
    bg: "rgba(245, 158, 11, 0.1)",
    borderColor: "rgba(245, 158, 11, 0.3)",
    icon: "⏰",
  },
  completed: {
    label: "Completed",
    color: "#6366f1",
    bg: "rgba(99, 102, 241, 0.1)",
    borderColor: "rgba(99, 102, 241, 0.3)",
    icon: "✅",
  },
  cancelled: {
    label: "Cancelled",
    color: "#ef4444",
    bg: "rgba(239, 68, 68, 0.1)",
    borderColor: "rgba(239, 68, 68, 0.3)",
    icon: "✕",
  },
  default: {
    label: "Unknown",
    color: "#9ca3af",
    bg: "rgba(156, 163, 175, 0.1)",
    borderColor: "rgba(156, 163, 175, 0.3)",
    icon: "?",
  },
};

const getStatusConfig = (status) => {
  const key = (status || "").toLowerCase();
  return statusConfig[key] || statusConfig.default;
};

// Custom hook for search and filtering
const useConversationSearch = (conversations) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  
  const filteredConversations = useMemo(() => {
    let filtered = [...conversations];
    
    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(c => 
        c.patientName?.toLowerCase().includes(search) ||
        c.doctorName?.toLowerCase().includes(search) ||
        c.serviceName?.toLowerCase().includes(search) ||
        c.lastMessagePreview?.toLowerCase().includes(search)
      );
    }
    
    // Apply status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter(c => {
        if (filterStatus === "closed") return c.isClosed;
        if (filterStatus === "unread") {
          const unread = c.unreadDoctor || c.unreadPatient || 0;
          return unread > 0;
        }
        return c.appointmentStatus?.toLowerCase() === filterStatus;
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === "recent") {
        const ta = getTimestamp(a);
        const tb = getTimestamp(b);
        return tb - ta;
      }
      if (sortBy === "unread") {
        const unreadA = a.unreadDoctor || a.unreadPatient || 0;
        const unreadB = b.unreadDoctor || b.unreadPatient || 0;
        return unreadB - unreadA;
      }
      if (sortBy === "name") {
        const nameA = a.patientName || a.doctorName || "";
        const nameB = b.patientName || b.doctorName || "";
        return nameA.localeCompare(nameB);
      }
      return 0;
    });
    
    return filtered;
  }, [conversations, searchTerm, filterStatus, sortBy]);
  
  return {
    searchTerm,
    setSearchTerm,
    filterStatus,
    setFilterStatus,
    sortBy,
    setSortBy,
    filteredConversations
  };
};

// Helper function to get timestamp
const getTimestamp = (conv) => {
  const timestamp = 
    conv.lastMessageAt?.toDate?.() ||
    conv.lastMessageAt ||
    conv.createdAt?.toDate?.() ||
    conv.createdAt ||
    0;
  return timestamp?.valueOf?.() ?? timestamp;
};

// Helper function to format time with enhanced options
function formatTime(date) {
  const now = new Date();
  const diff = now - new Date(date);
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 7) {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: now.getFullYear() !== new Date(date).getFullYear() ? "numeric" : undefined
    });
  }
  if (days === 1) return "Yesterday";
  if (days > 1) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return "Just now";
}

// Main component
export default function TelehealthInbox({ mode = "doctor" }) {
  const nav = useNavigate();
  const [user, setUser] = useState(() => auth.currentUser || null);
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);
  
  // Use the search and filter hook
  const {
    searchTerm,
    setSearchTerm,
    filterStatus,
    setFilterStatus,
    sortBy,
    setSortBy,
    filteredConversations
  } = useConversationSearch(conversations);

  // Stats calculation
  const stats = useMemo(() => {
    const totalUnread = conversations.reduce((sum, c) => {
      const unread = mode === "doctor" ? c.unreadDoctor || 0 : c.unreadPatient || 0;
      return sum + unread;
    }, 0);
    
    const statusCounts = conversations.reduce((acc, c) => {
      const status = c.appointmentStatus?.toLowerCase() || "unknown";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    
    return { totalUnread, statusCounts };
  }, [conversations, mode]);

  // Auth state management
  useEffect(() => {
    isMounted.current = true;
    const unsubAuth = onAuthStateChanged(auth, (u) => {
      if (!isMounted.current) return;
      setUser(u);
      if (!u) {
        setConversations([]);
        setActive(null);
      }
    });
    return () => {
      isMounted.current = false;
      unsubAuth?.();
    };
  }, []);

  // Conversations listener with error handling
  useEffect(() => {
    if (!user) {
      setConversations([]);
      setActive(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    const field = mode === "doctor" ? "doctorId" : "patientId";
    const convRef = collection(db, "conversations");
    const q = query(
      convRef, 
      where(field, "==", user.uid),
      orderBy("lastMessageAt", "desc"),
      limit(50) // Limit for performance
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        if (!isMounted.current) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setConversations(list);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("TelehealthInbox error:", err);
        if (isMounted.current) {
          setError(err.message || "Failed to load conversations");
          setLoading(false);
        }
      }
    );

    return () => unsub();
  }, [user, mode]);

  // Mark as read with debounce
  useEffect(() => {
    if (!active || !user) return;
    
    const timeoutId = setTimeout(() => {
      const field = mode === "doctor" ? "unreadDoctor" : "unreadPatient";
      const ref = doc(db, "conversations", active.id);
      
      updateDoc(ref, { [field]: 0 }).catch((err) =>
        console.warn("Failed to mark conversation read:", err?.message || err)
      );
    }, 500); // Debounce for 500ms
    
    return () => clearTimeout(timeoutId);
  }, [active, mode, user]);

  // Start or open call with loading state
  const [callLoading, setCallLoading] = useState(false);
  
  const startOrOpenCall = useCallback(
    async (conv) => {
      if (!user) {
        alert("Please log in first.");
        return;
      }

      const meIsParticipant =
        user.uid === conv.patientId || user.uid === conv.doctorId;
      if (!meIsParticipant) {
        alert("You don't have access to this call.");
        return;
      }

      setCallLoading(true);
      try {
        const callId = conv.appointmentId || conv.id;
        const callRef = doc(db, "calls", callId);
        const snap = await getDoc(callRef);

        if (!snap.exists()) {
          await setDoc(callRef, {
            createdAt: Date.now(),
            status: "pending",
            doctorId: conv.doctorId,
            patientId: conv.patientId,
            appointmentId: conv.appointmentId || conv.id,
            offer: null,
            answer: null,
          });
        }

        nav(`/call/${callId}`);
      } catch (err) {
        console.error("Failed to start call:", err);
        alert("Failed to start video call. Please try again.");
      } finally {
        setCallLoading(false);
      }
    },
    [nav, user]
  );

  // Fullscreen state for modal
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Clear active chat
  const clearActiveChat = useCallback(() => {
    setActive(null);
    setIsFullscreen(false);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Escape" && active) {
        clearActiveChat();
      }
      if (e.ctrlKey && e.key === "f") {
        e.preventDefault();
        document.getElementById("search-input")?.focus();
      }
      // Toggle fullscreen with F11
      if (e.key === "F11" && active) {
        e.preventDefault();
        setIsFullscreen(prev => !prev);
      }
    };
    
    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [active, clearActiveChat]);

  if (!user) {
    return (
      <div style={styles.authContainer}>
        <div style={styles.authCard}>
          <svg
            width="56"
            height="56"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: "#e5e7eb", marginBottom: 20 }}
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <h3 style={styles.authTitle}>Sign In Required</h3>
          <p style={styles.authText}>
            Please log in to view your messages and start conversations
          </p>
          <button 
            style={styles.authButton}
            onClick={() => nav("/login")}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const title = mode === "doctor" ? "Patient Messages" : "Doctor Messages";

  return (
    <>
      <div style={styles.container}>
        {/* Enhanced Header */}
        <div style={styles.header}>
          <div style={styles.headerTop}>
            <h3 style={styles.title}>{title}</h3>
            <div style={styles.headerBadges}>
              {stats.totalUnread > 0 && (
                <span style={styles.unreadTotalBadge}>
                  {stats.totalUnread} unread
                </span>
              )}
              <span style={styles.badge}>
                {conversations.length} total
              </span>
            </div>
          </div>
          
          {/* Search and Filter Bar */}
          <div style={styles.filterBar}>
            <div style={styles.searchContainer}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={styles.searchIcon}
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                id="search-input"
                type="text"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  style={styles.clearButton}
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="all">All Status</option>
              <option value="unread">Unread</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="closed">Closed</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="recent">Most Recent</option>
              <option value="unread">Unread First</option>
              <option value="name">By Name</option>
            </select>
          </div>
        </div>

        {/* Content Area */}
        {error ? (
          <div style={styles.errorState}>
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              style={{ color: "#ef4444", marginBottom: 16 }}
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p style={{ color: "#ef4444", margin: "0 0 16px" }}>
              {error}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={styles.retryButton}
            >
              Retry
            </button>
          </div>
        ) : loading ? (
          <div style={styles.loading}>
            <div style={styles.loadingSpinner} />
            <span>Loading conversations...</span>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div style={styles.emptyState}>
            {conversations.length > 0 ? (
              <>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  style={{ color: "#e5e7eb", marginBottom: 16 }}
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <p style={{ color: "#6b7280", margin: 0 }}>
                  No conversations match your filters
                </p>
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setFilterStatus("all");
                  }}
                  style={styles.clearFiltersButton}
                >
                  Clear Filters
                </button>
              </>
            ) : (
              <>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ color: "#e5e7eb", marginBottom: 16 }}
                >
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
                <p style={{ color: "#6b7280", margin: 0, textAlign: "center" }}>
                  No conversations yet
                  <br />
                  <span style={{ fontSize: 13, color: "#9ca3af" }}>
                    Start a chat from an appointment
                  </span>
                </p>
              </>
            )}
          </div>
        ) : (
          <div style={styles.list}>
            {filteredConversations.map((c) => {
              const closed = !!c.isClosed;
              const status = getStatusConfig(c.appointmentStatus);
              const otherName =
                mode === "doctor"
                  ? c.patientName || "Unknown patient"
                  : c.doctorName || "Unknown doctor";

              const unread =
                mode === "doctor" ? c.unreadDoctor || 0 : c.unreadPatient || 0;
              const hasUnread = unread > 0;

              const lastMessageTime = c.lastMessageAt?.toDate?.() || c.lastMessageAt;
              const timeString = lastMessageTime
                ? formatTime(lastMessageTime)
                : "";

              const isActive = active?.id === c.id;

              return (
                <div
                  key={c.id}
                  className="conversation-item"
                  style={{
                    ...styles.conversationItem,
                    ...(hasUnread ? styles.conversationItemUnread : {}),
                    ...(isActive ? styles.conversationItemActive : {}),
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setActive(c)}
                    style={styles.conversationButton}
                    aria-label={`Open conversation with ${otherName}`}
                  >
                    {/* Enhanced Avatar */}
                    <div style={styles.avatarContainer}>
                      <div
                        style={{
                          ...styles.avatar,
                          ...(hasUnread ? styles.avatarUnread : {}),
                        }}
                      >
                        {otherName?.[0]?.toUpperCase() || "?"}
                      </div>
                      {hasUnread && <div style={styles.avatarDot} />}
                    </div>

                    {/* Content */}
                    <div style={styles.conversationContent}>
                      <div style={styles.conversationHeader}>
                        <span
                          style={{
                            ...styles.conversationName,
                            ...(hasUnread ? { fontWeight: 600 } : {}),
                          }}
                        >
                          {otherName}
                        </span>
                        {timeString && (
                          <span style={styles.timestamp}>{timeString}</span>
                        )}
                      </div>

                      <div style={styles.conversationPreview}>
                        <div style={styles.serviceRow}>
                          <span style={styles.serviceName}>
                            {c.serviceName || "Consultation"}
                          </span>
                          {c.lastMessagePreview && (
                            <span style={styles.separator}>·</span>
                          )}
                          <span style={styles.statusIcon}>
                            {status.icon}
                          </span>
                        </div>
                        {c.lastMessagePreview && (
                          <div
                            style={{
                              ...styles.messagePreview,
                              ...(hasUnread ? { color: "#374151", fontWeight: 500 } : {}),
                            }}
                          >
                            {c.lastMessagePreview}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Actions */}
                  <div style={styles.conversationActions}>
                    {hasUnread && (
                      <div style={styles.unreadBadge}>{unread > 99 ? "99+" : unread}</div>
                    )}

                    <div
                      style={{
                        ...styles.statusBadge,
                        background: status.bg,
                        color: status.color,
                        border: `1px solid ${status.borderColor}`,
                      }}
                    >
                      {closed ? "Closed" : status.label}
                    </div>

                    
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal Chat Display */}
      {active && (
        <>
          <div 
            style={styles.modalBackdrop} 
            onClick={clearActiveChat}
          />
          <div style={styles.modalContainer}>
            <div style={{
              ...styles.modal,
              ...(isFullscreen ? styles.modalFullscreen : {})
            }}>
              <div style={styles.modalHeader}>
                <div style={styles.modalHeaderLeft}>
                  <div style={styles.modalAvatar}>
                    {(mode === "doctor" 
                      ? active.patientName?.[0] 
                      : active.doctorName?.[0])?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <h3 style={styles.modalTitle}>
                      {mode === "doctor" 
                        ? active.patientName || "Patient"
                        : active.doctorName || "Doctor"}
                    </h3>
                    <div style={styles.modalMeta}>
                      <span>{active.serviceName || "Consultation"}</span>
                      <span style={styles.modalSeparator}>•</span>
                      <span style={{
                        ...styles.modalStatus,
                        color: getStatusConfig(active.appointmentStatus).color
                      }}>
                        {getStatusConfig(active.appointmentStatus).icon} {getStatusConfig(active.appointmentStatus).label}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div style={styles.modalHeaderRight}>
                  
                  
                  <button
                    onClick={clearActiveChat}
                    style={styles.modalCloseButton}
                    title="Close (Esc)"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div style={styles.modalContent}>
                <AppointmentChat
                  appointmentId={active.appointmentId}
                  doctorId={active.doctorId}
                  patientId={active.patientId}
                  appointmentStatus={active.appointmentStatus}
                  doctorName={active.doctorName}
                  patientName={active.patientName}
                  serviceName={active.serviceName}
                  conversationId={active.id}
                  mode={mode}
                  onClose={clearActiveChat}
                  embedded={true}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// Enhanced Styles preserving the original color scheme
const styles = {
  container: {
    borderRadius: 12,
    background: "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(0, 0, 0, 0.05)",
    overflow: "hidden",
    maxWidth: "100%",
    maxHeight: "100vh",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  },

  header: {
    borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
    background: "rgba(255, 255, 255, 0.5)",
    backdropFilter: "blur(8px)",
    flexShrink: 0,
  },

  headerTop: {
    padding: "16px 20px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    color: "#111827",
    letterSpacing: "-0.025em",
  },

  headerBadges: {
    display: "flex",
    gap: 8,
    alignItems: "center",
  },

  badge: {
    minWidth: 20,
    height: 22,
    borderRadius: 11,
    padding: "0 10px",
    fontSize: 12,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0, 0, 0, 0.06)",
    color: "#6b7280",
  },

  unreadTotalBadge: {
    minWidth: 20,
    height: 22,
    borderRadius: 11,
    padding: "0 10px",
    fontSize: 12,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#3b82f6",
    color: "#ffffff",
  },

  filterBar: {
  padding: "0 20px 16px",
  display: "flex",
  flexWrap: "wrap",           // ✅ allow wrapping
  rowGap: 8,                  // ✅ vertical gap between rows
  columnGap: 8,
  alignItems: "center",
},

  searchContainer: {
  flex: "1 1 100%",           // ✅ always takes full width first row
  position: "relative",
  display: "flex",
  alignItems: "center",
},


  searchIcon: {
    position: "absolute",
    left: 12,
    color: "#9ca3af",
    pointerEvents: "none",
  },

  searchInput: {
    flex: 1,
    height: 36,
    paddingLeft: 36,
    paddingRight: 32,
    border: "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    transition: "border-color 0.2s",
    background: "rgba(255, 255, 255, 0.8)",
  },

  clearButton: {
    position: "absolute",
    right: 8,
    background: "transparent",
    border: "none",
    padding: 4,
    cursor: "pointer",
    color: "#9ca3af",
    fontSize: 14,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

 filterSelect: {
  height: 36,
  padding: "0 12px",
  border: "1px solid rgba(0, 0, 0, 0.1)",
  borderRadius: 8,
  fontSize: 14,
  outline: "none",
  background: "rgba(255, 255, 255, 0.8)",
  cursor: "pointer",
  flex: "1 1 140px",          // ✅ two selects share the second row
  minWidth: 0,                // ✅ allow shrinking inside small panel
},


  loading: {
    padding: 48,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    fontSize: 14,
    color: "#9ca3af",
  },

  loadingSpinner: {
    width: 32,
    height: 32,
    border: "3px solid #f3f4f6",
    borderTopColor: "#3b82f6",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },

  miniSpinner: {
    width: 14,
    height: 14,
    border: "2px solid rgba(59, 130, 246, 0.2)",
    borderTopColor: "#3b82f6",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },

  emptyState: {
    padding: 48,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    flex: 1,
  },

  errorState: {
    padding: 48,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    flex: 1,
  },

  retryButton: {
    padding: "8px 16px",
    borderRadius: 6,
    border: "1px solid #ef4444",
    background: "white",
    color: "#ef4444",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
  },

  clearFiltersButton: {
    marginTop: 16,
    padding: "8px 16px",
    borderRadius: 6,
    border: "1px solid #3b82f6",
    background: "white",
    color: "#3b82f6",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
  },

  list: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    minHeight: 0,
  },

  conversationItem: {
    borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "14px 20px",
    transition: "all 0.2s ease",
    position: "relative",
    background: "transparent",
  },

  conversationItemUnread: {
    background: "rgba(59, 130, 246, 0.05)",
    borderLeft: "3px solid #3b82f6",
    paddingLeft: 17,
  },

  conversationItemActive: {
    background: "rgba(99, 102, 241, 0.08)",
  },

  conversationButton: {
    flex: 1,
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    background: "transparent",
    border: 0,
    padding: 0,
    cursor: "pointer",
    textAlign: "left",
  },

  avatarContainer: {
    position: "relative",
    flexShrink: 0,
  },

  avatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    fontWeight: 500,
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },

  avatarUnread: {
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
  },

  avatarDot: {
    position: "absolute",
    top: 0,
    right: 0,
    width: 10,
    height: 10,
    borderRadius: 5,
    background: "#ef4444",
    border: "2px solid white",
  },

  conversationContent: {
    flex: 1,
    minWidth: 0,
  },

  conversationHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },

  conversationName: {
    fontSize: 14,
    fontWeight: 500,
    color: "#111827",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  timestamp: {
    fontSize: 12,
    color: "#9ca3af",
    flexShrink: 0,
    marginLeft: 8,
  },

  conversationPreview: {
    fontSize: 13,
    color: "#6b7280",
    lineHeight: 1.5,
  },

  serviceRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },

  serviceName: {
    color: "#6b7280",
    fontWeight: 500,
  },

  statusIcon: {
    fontSize: 12,
    marginLeft: 4,
  },

  separator: {
    color: "#d1d5db",
    margin: "0 4px",
  },

  messagePreview: {
    display: "block",
    marginTop: 4,
    lineHeight: 1.4,
    wordBreak: "break-word",
    maxHeight: "2.8em",
    overflow: "hidden",
    position: "relative",
  },

  conversationActions: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    flexShrink: 0,
    paddingTop: 2,
  },

  unreadBadge: {
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    padding: "0 7px",
    fontSize: 11,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#3b82f6",
    color: "#ffffff",
  },

  statusBadge: {
    fontSize: 11,
    padding: "5px 10px",
    borderRadius: 12,
    fontWeight: 500,
    whiteSpace: "nowrap",
  },

  videoButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "1px solid rgba(59, 130, 246, 0.2)",
    background: "rgba(59, 130, 246, 0.05)",
    color: "#3b82f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  // Modal Styles
  modalBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.6)",
    backdropFilter: "blur(4px)",
    zIndex: 9998,
    animation: "fadeIn 0.2s ease",
  },

  modalContainer: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    zIndex: 9999,
  },

  modal: {
    width: "90%",
    maxWidth: 900,
    height: "85vh",
    maxHeight: 700,
    background: "white",
    borderRadius: 16,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    animation: "scaleIn 0.3s ease",
  },

  modalFullscreen: {
    width: "100%",
    maxWidth: "100%",
    height: "100vh",
    maxHeight: "100vh",
    borderRadius: 0,
  },

  modalHeader: {
    padding: "20px 24px",
    borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
  },

  modalHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    flex: 1,
  },

  modalAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    fontWeight: 600,
  },

  modalTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    color: "#111827",
  },

  modalMeta: {
    fontSize: 13,
    color: "#6b7280",
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  modalSeparator: {
    color: "#d1d5db",
  },

  modalStatus: {
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },

  modalHeaderRight: {
    display: "flex",
    gap: 8,
    alignItems: "center",
  },

  modalVideoButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    border: "1px solid rgba(59, 130, 246, 0.2)",
    background: "rgba(59, 130, 246, 0.05)",
    color: "#3b82f6",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
  },

  modalExpandButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    border: "1px solid rgba(0, 0, 0, 0.1)",
    background: "white",
    color: "#6b7280",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
  },

  modalCloseButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    border: "none",
    background: "rgba(239, 68, 68, 0.05)",
    color: "#ef4444",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
  },

  modalContent: {
    flex: 1,
    overflow: "hidden",
  },

  authContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    padding: 20,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },

  authCard: {
    background: "white",
    borderRadius: 16,
    padding: 40,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    maxWidth: 400,
    width: "100%",
    textAlign: "center",
  },

  authTitle: {
    margin: "0 0 12px",
    fontSize: 24,
    fontWeight: 600,
    color: "#111827",
  },

  authText: {
    margin: "0 0 24px",
    fontSize: 14,
    color: "#6b7280",
    lineHeight: 1.5,
  },

  authButton: {
    padding: "12px 24px",
    borderRadius: 8,
    border: "none",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
    transition: "transform 0.2s ease",
  },
};

// Add CSS animations and enhanced styles
if (typeof document !== "undefined" && !document.querySelector("#telehealth-inbox-styles")) {
  const style = document.createElement("style");
  style.id = "telehealth-inbox-styles";
  style.textContent = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    
    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateX(20px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    
    @keyframes slideInRight {
      from {
        transform: translateX(100%);
      }
      to {
        transform: translateX(0);
      }
    }
    
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
    
    @keyframes scaleIn {
      from {
        opacity: 0;
        transform: scale(0.95);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
    
    /* Custom scrollbar with preserved colors */
    *::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    
    *::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.02);
      border-radius: 4px;
    }
    
    *::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 4px;
    }
    
    *::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 0, 0, 0.2);
    }
    
    /* Input focus styles */
    input:focus, select:focus {
      border-color: #3b82f6 !important;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    
    /* Conversation item hover effects */
    .conversation-item {
      transition: all 0.2s ease;
    }
    
    .conversation-item:hover {
      background: rgba(0, 0, 0, 0.02) !important;
    }
    
    /* Video button hover with preserved colors */
    .video-button {
      transition: all 0.2s ease;
    }
    
    .video-button:hover:not(:disabled) {
      background: rgba(59, 130, 246, 0.15) !important;
      border-color: rgba(59, 130, 246, 0.4) !important;
      transform: translateY(-1px);
      box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.2);
    }
    
    .video-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    /* Auth button hover */
    button[style*="linear-gradient"]:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(103, 126, 234, 0.3);
    }
    
    /* Mode button hover */
    button:hover {
      transition: all 0.2s ease;
    }
    
    /* Panel close button hover */
    button[style*="rgba(0, 0, 0, 0.05)"]:hover {
      background: rgba(0, 0, 0, 0.1) !important;
    }
    
    /* Modal close button hover */
    button[style*="rgba(239, 68, 68, 0.05)"]:hover {
      background: rgba(239, 68, 68, 0.1) !important;
    }
    
    /* Split view conversation item hover */
    div[style*="splitConversationItem"]:hover {
      background: rgba(0, 0, 0, 0.03) !important;
    }
    
    /* Header video button hover */
    button[style*="rgba(59, 130, 246, 0.05)"]:hover:not(:disabled) {
      background: rgba(59, 130, 246, 0.1) !important;
      border-color: rgba(59, 130, 246, 0.3) !important;
      transform: translateY(-1px);
    }
    
    /* Smooth transitions */
    * {
      transition: background-color 0.2s ease, border-color 0.2s ease;
    }
    
    /* Responsive adjustments */
    @media (max-width: 768px) {
      .side-panel {
        width: 100vw !important;
      }
      
      .modal {
        width: 95% !important;
        height: 90vh !important;
      }
    }
  `;
  document.head.appendChild(style);
}